import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { banners } from "@/mocks/banners";
import { priceItems } from "@/mocks/prices";
import { directionClass, formatPercent, formatPrice } from "@/utils/format";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % banners.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const spotlight = priceItems.find((item) => item.id === "london-gold") ?? priceItems[0];

  return (
    <section className="relative w-full h-[460px] md:h-[600px] overflow-hidden bg-accent-950">
      {banners.map((banner, index) => (
        <div
          key={banner.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={banner.image}
            alt={banner.title}
            title={`${banner.title} - 黄金行情`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-accent-950 via-accent-950/85 to-accent-950/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-accent-950/90 via-transparent to-accent-950/40" />
        </div>
      ))}

      <div className="relative h-full w-full flex items-center px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 w-full items-center">
          <div className="lg:col-span-7">
            {banners[active] && (
              <div key={banners[active].id} className="animate-fade-up">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500/15 border border-primary-500/30 text-primary-300 text-xs font-label mb-5">
                  <i className="ri-flashlight-line" />
                  {banners[active].tag}
                  <span className="text-foreground-400">· {banners[active].time}</span>
                </span>
                <h2 className="font-heading font-bold text-foreground-50 text-3xl md:text-5xl leading-tight text-balance mb-5">
                  {banners[active].title}
                </h2>
                <p className="text-foreground-200 text-sm md:text-base leading-relaxed max-w-2xl mb-8">
                  {banners[active].description}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to={banners[active].link}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-accent-950 text-sm font-medium whitespace-nowrap transition-colors"
                  >
                    查看详情
                    <i className="ri-arrow-right-line" />
                  </Link>
                  <Link
                    to="/category/gold-price"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-white/20 text-foreground-50 hover:bg-white/10 text-sm font-medium whitespace-nowrap transition-colors"
                  >
                    全部行情
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-5 hidden lg:flex justify-end">
            <div className="w-full max-w-sm rounded-lg border border-white/10 bg-white/5 backdrop-blur-md p-6 animate-fade-in">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-xs text-foreground-400 mb-1">国际现货黄金</p>
                  <p className="text-sm font-label text-foreground-100">XAU/USD · 美元/盎司</p>
                </div>
                <span className="w-9 h-9 rounded-full bg-primary-500/15 flex items-center justify-center text-primary-400">
                  <i className="ri-line-chart-line" />
                </span>
              </div>
              <p className="font-heading font-bold text-3xl text-foreground-50 mb-2">
                {formatPrice(spotlight.price)}
              </p>
              <p className={`flex items-center gap-1 text-sm mb-6 ${directionClass(spotlight.changePercent)}`}>
                <i className={spotlight.changePercent >= 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"} />
                {formatPrice(spotlight.change)} ({formatPercent(spotlight.changePercent)})
              </p>
              <div className="grid grid-cols-2 gap-4 pt-5 border-t border-white/10 text-sm">
                <div>
                  <p className="text-xs text-foreground-400 mb-1">今日最高</p>
                  <p className="text-foreground-100">{formatPrice(spotlight.high)}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-400 mb-1">今日最低</p>
                  <p className="text-foreground-100">{formatPrice(spotlight.low)}</p>
                </div>
              </div>
              <p className="mt-5 text-[11px] text-foreground-400">数据均为演示数据 · 更新于 {spotlight.updateTime}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-4 md:left-6 flex items-center gap-2 z-10">
        {banners.map((banner, index) => (
          <button
            key={banner.id}
            type="button"
            aria-label={`切换到第 ${index + 1} 张`}
            onClick={() => setActive(index)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              index === active ? "w-8 bg-primary-400" : "w-4 bg-white/30 hover:bg-white/50"
            }`}
          />
        ))}
      </div>

      <div className="absolute bottom-6 right-4 md:right-6 flex items-center gap-2 z-10">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => setActive((prev) => (prev - 1 + banners.length) % banners.length)}
          className="w-10 h-10 rounded-full border border-white/20 text-foreground-50 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => setActive((prev) => (prev + 1) % banners.length)}
          className="w-10 h-10 rounded-full border border-white/20 text-foreground-50 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>
    </section>
  );
}